import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../services/firebase';
import formData from '../utils/formConfig';
import Snackbar from '../components/shared/Snackbar';
import { FormValues, SnackbarType } from '../types';
import { validateLoginForm } from '../utils/validation';
import { getAuthErrorMessage, isFirebaseAuthError } from '../utils/firebase';
import { ROUTES } from '../constants/routes';

/**
 * LoginPage component
 * Handles user authentication
 */
const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [formValues, setFormValues] = useState<FormValues>({ email: '', password: '' });
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarType, setSnackbarType] = useState<SnackbarType>('error');

  const showNotification = (message: string, type: SnackbarType = 'error') => {
    setSnackbarMessage(message);
    setSnackbarType(type);
    setSnackbarOpen(true);
  };

  // Add animation effect when component mounts
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormValues({
      ...formValues,
      [e.target.id]: e.target.value
    });
  };

  const handleLoginSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    // Validate form before submission
    const validation = validateLoginForm(formValues);
    if (!validation.isValid) {
      showNotification(validation.message || 'Please check your input', 'error');
      return;
    }

    setIsLoading(true);

    try {
      const userCredential = await auth.signInWithEmailAndPassword(formValues.email, formValues.password);
      const user = userCredential.user;

      if (user) {
        if (user.emailVerified) {
          showNotification('Successfully logged in!', 'success');
          // Navigation is handled by useAuth hook redirecting to dashboard
        } else {
          navigate(ROUTES.EMAIL_VERIFICATION);
          await auth.signOut();
          showNotification('Please verify your email before logging in.', 'warning');
        }
      }
    } catch (error) {
      let errorMessage = 'An unexpected error occurred. Please try again.';

      if (isFirebaseAuthError(error)) {
        errorMessage = getAuthErrorMessage(error);
      }

      showNotification(errorMessage, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className={`auth-content ${isVisible ? 'content-visible' : ''}`}>
        <form className="auth-form" onSubmit={handleLoginSubmit}>
          <Snackbar
            open={snackbarOpen}
            message={snackbarMessage}
            type={snackbarType}
            onClose={() => setSnackbarOpen(false)}
            autoHideDuration={4000}
          />
          <h2 className="auth-title">Login</h2>
          {formData.loginFormData.map((field) => (
            <div className="form-group" key={field.id}>
              <label htmlFor={field.id}>{field.label}</label>
              <input
                type={field.type}
                id={field.id}
                placeholder={field.placeholder}
                value={formValues[field.id] || ''}
                onChange={handleChange}
                disabled={isLoading}
                required
              />
            </div>
          ))}
          <button
            type="submit"
            className="auth-button"
            disabled={isLoading}
          >
            {isLoading ? 'Logging in...' : 'Login'}
          </button>
          <p className="auth-switch">
            Don't have an account?{' '}
            <button
              type="button"
              className="link-button"
              onClick={() => navigate(ROUTES.REGISTER)}
              disabled={isLoading}
            >
              Create Account
            </button>
          </p>
        </form>
      </div>
    </div>
  );
};

export default LoginPage;
